// src/pages/WireMeshSculpture2.js  (a.k.a. WireMeshGenerator2)
import React, { useRef, useState, useCallback, useEffect, useMemo } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import { sliceModelXZ, sliceModelYZ } from "../utils/sliceModel";
import useVisualHelpers from "../hooks/useVisualHelpers";
import TutorialWindow from "../components/TutorialWindow";
import { wireMeshTutorial } from "../utils/tutorialContents";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import "../styles/common.css";

/* --------------------- geometry utils & cleaners --------------------- */

const dist3 = (a, b) =>
  Math.hypot(b.x - a.x, (b.y ?? 0) - (a.y ?? 0), (b.z ?? 0) - (a.z ?? 0));

const angleDegAt = (p0, p1, p2) => {
  const v1 = new THREE.Vector3(p0.x - p1.x, (p0.y ?? 0) - (p1.y ?? 0), (p0.z ?? 0) - (p1.z ?? 0));
  const v2 = new THREE.Vector3(p2.x - p1.x, (p2.y ?? 0) - (p1.y ?? 0), (p2.z ?? 0) - (p1.z ?? 0));
  if (v1.lengthSq() < 1e-8 || v2.lengthSq() < 1e-8) return 0;
  v1.normalize(); v2.normalize();
  const dot = THREE.MathUtils.clamp(v1.dot(v2), -1, 1);
  return (Math.acos(dot) * 180) / Math.PI; // 0° = straight
};

// Ramer–Douglas–Peucker (3D)
const rdp3D = (pts, eps) => {
  if (!pts || pts.length <= 2 || eps <= 0) return pts.slice();
  const keep = new Array(pts.length).fill(false);
  keep[0] = keep[pts.length - 1] = true;

  const perpDist = (p, a, b) => {
    const ab = new THREE.Vector3(b.x - a.x, (b.y ?? 0) - (a.y ?? 0), (b.z ?? 0) - (a.z ?? 0));
    const ap = new THREE.Vector3(p.x - a.x, (p.y ?? 0) - (a.y ?? 0), (p.z ?? 0) - (a.z ?? 0));
    const t = THREE.MathUtils.clamp(ab.dot(ap) / (ab.lengthSq() || 1), 0, 1);
    const proj = new THREE.Vector3(a.x + ab.x * t, (a.y ?? 0) + ab.y * t, (a.z ?? 0) + ab.z * t);
    return dist3(p, proj);
  };

  const stack = [[0, pts.length - 1]];
  while (stack.length) {
    const [i, j] = stack.pop();
    let maxD = -1, idx = -1;
    for (let k = i + 1; k < j; k++) {
      const d = perpDist(pts[k], pts[i], pts[j]);
      if (d > maxD) { maxD = d; idx = k; }
    }
    if (maxD > eps && idx > 0) {
      keep[idx] = true;
      stack.push([i, idx], [idx, j]);
    }
  }
  return pts.filter((_, i) => keep[i]);
};

// equal-spacing resample
const resampleEqual = (pts, spacing) => {
  if (!pts || pts.length < 2 || spacing <= 0) return pts ? pts.slice() : [];
  const S = [0];
  for (let i = 1; i < pts.length; i++) S[i] = S[i - 1] + dist3(pts[i - 1], pts[i]);
  const total = S[S.length - 1];
  if (total <= 0) return [pts[0]];
  const N = Math.max(2, Math.round(total / spacing));
  const out = [pts[0]];
  for (let k = 1; k < N - 1; k++) {
    const target = (k * total) / (N - 1);
    let i = 1; while (i < S.length && S[i] < target) i++;
    const s0 = S[i - 1], s1 = S[i];
    const t = (target - s0) / (s1 - s0 + 1e-12);
    const a = pts[i - 1], b = pts[i];
    out.push({ x: a.x + (b.x - a.x) * t, y: (a.y ?? 0) + ((b.y ?? 0) - (a.y ?? 0)) * t, z: (a.z ?? 0) + ((b.z ?? 0) - (a.z ?? 0)) * t });
  }
  out.push(pts[pts.length - 1]);
  return out;
};

// polyline preprocessing pipeline
const preprocessPolyline3 = (pts, {
  mergeDistMM  = 1.0,   // collapse near-duplicates
  minSegGenMM  = 10.0,  // remove very short segments
  collinearDeg = 10.0,  // remove tiny bends (< this)
  rdpEpsMM     = 2.0,   // geometric simplification (0 to disable)
  resampleMM   = 6.0,   // even spacing (0 to skip)
} = {}) => {
  if (!pts || pts.length < 2) return pts ? pts.slice() : [];

  // → array of plain {x,y,z}
  let out = pts.map(p => ({ x: p.x, y: p.y ?? 0, z: p.z ?? 0 }));

  // 1) drop near-duplicates
  {
    const tmp = [out[0]];
    for (let i = 1; i < out.length; i++) if (dist3(out[i], tmp[tmp.length - 1]) > mergeDistMM) tmp.push(out[i]);
    out = tmp;
  }
  if (out.length < 2) return out;

  // 2) remove very short segments (iterate a few times)
  for (let iter = 0; iter < 4 && out.length > 2; iter++) {
    let changed = false;
    const tmp = [out[0]];
    for (let i = 1; i < out.length; i++) {
      if (dist3(out[i - 1], out[i]) >= minSegGenMM) tmp.push(out[i]);
      else changed = true;
    }
    out = tmp;
    if (!changed) break;
  }

  // 3) remove near-collinear middle points
  if (out.length > 2) {
    const tmp = [out[0]];
    for (let i = 1; i < out.length - 1; i++) {
      const ang = angleDegAt(out[i - 1], out[i], out[i + 1]);
      if (ang >= collinearDeg) tmp.push(out[i]);
    }
    tmp.push(out[out.length - 1]);
    out = tmp;
  }

  // 4) optional RDP simplify
  if (rdpEpsMM > 0 && out.length > 2) out = rdp3D(out, rdpEpsMM);

  // 5) resample equal spacing
  if (resampleMM > 0 && out.length > 2) out = resampleEqual(out, resampleMM);

  // back to Vector3s
  return out.map(p => new THREE.Vector3(p.x, p.y, p.z));
};

// put this helper in WireMeshSculpture2.js (top-level, next to other utils)
function buildSafeCurvePath(points, eps = 1e-4) {
  // 1) de-duplicate consecutive points
  const uniq = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (uniq.length === 0 || uniq[uniq.length - 1].distanceTo(p) > eps) {
      uniq.push(p.clone());
    }
  }

  // if we don’t have at least two distinct points, bail
  if (uniq.length < 2) return null;

  // 2) build non-degenerate segments
  const path = new THREE.CurvePath();
  for (let i = 1; i < uniq.length; i++) {
    if (uniq[i - 1].distanceTo(uniq[i]) > eps) {
      path.add(new THREE.LineCurve3(uniq[i - 1], uniq[i]));
    }
  }

  // 3) close if needed (and non-zero)
  const first = uniq[0], last = uniq[uniq.length - 1];
  if (uniq.length > 2 && last.distanceTo(first) > eps) {
    path.add(new THREE.LineCurve3(last, first));
  }

  // if still empty, bail
  if (!path.curves || path.curves.length === 0) return null;
  return path;
}

/* --------------------- bend/length analyzer (wide baseline + clustering) --------------------- */

const analyzeWires = (
  curves,
  {
    minCurveLenMM   = 20,  // whole-curve length threshold
    minAngleDeg     = 10,  // report bends smaller than this
    legMinMM        = 15,  // each side of the bend must be ≥ this
    windowMM        = 30,  // measure angle using points ±windowMM away
    sampleMM        = 6,   // sampling step for spaced points
    clusterMM       = 35,  // merge multiple samples within this distance
    capPerCurve     = 6,   // max bend warnings per curve
    label           = "wire",
  } = {}
) => {
  const issues = [];
  const toDeg = (r) => (r * 180) / Math.PI;

  curves.forEach((path, idx) => {
    const wireIndex = idx + 1;

    // curve length
    let L = 0;
    try { L = path.getLength(); }
    catch {
      const tmp = path.getPoints(128);
      for (let i = 1; i < tmp.length; i++) L += dist3(tmp[i - 1], tmp[i]);
    }
    if (L < minCurveLenMM) {
      const msg = `${label} #${wireIndex}: total length ${L.toFixed(1)} mm (< ${minCurveLenMM} mm)`;
      console.warn("[Wire QA] Short curve:", msg);
      issues.push({ type: "short_curve", wireIndex, length: +L.toFixed(2), threshold: minCurveLenMM, message: msg });
      // keep going (we still might have a super-tight kink)
    }

    // sampling
    const nSamp = Math.max(12, Math.min(600, Math.round(L / Math.max(1, sampleMM))));
    const pts = path.getSpacedPoints(nSamp);
    if (!pts || pts.length < 3) return;

    // cumulative arc-lengths
    const S = new Array(pts.length).fill(0);
    for (let i = 1; i < pts.length; i++) S[i] = S[i - 1] + dist3(pts[i - 1], pts[i]);

    const step = Math.max(1, Math.round(windowMM / Math.max(1, sampleMM)));
    const raw = [];
    for (let i = step; i < pts.length - step; i++) {
      const pPrev = pts[i - step], p = pts[i], pNext = pts[i + step];
      const leg1 = dist3(pPrev, p), leg2 = dist3(p, pNext);
      if (leg1 < legMinMM || leg2 < legMinMM) continue;

      const v1 = new THREE.Vector3(pPrev.x - p.x, (pPrev.y ?? 0) - (p.y ?? 0), (pPrev.z ?? 0) - (p.z ?? 0)).normalize();
      const v2 = new THREE.Vector3(pNext.x - p.x, (pNext.y ?? 0) - (p.y ?? 0), (pNext.z ?? 0) - (p.z ?? 0)).normalize();
      const dot = THREE.MathUtils.clamp(v1.dot(v2), -1, 1);
      const angle = toDeg(Math.acos(dot)); // 0 = straight

      if (angle < minAngleDeg) raw.push({ i, s: S[i], angle });
    }

    if (raw.length === 0) return;

    // cluster nearby candidates (mm-space), keep worst angle in each
    const clusters = [];
    let curr = { startS: raw[0].s, best: raw[0] };
    for (let k = 1; k < raw.length; k++) {
      const r = raw[k];
      if (r.s - curr.best.s <= clusterMM) {
        if (r.angle < curr.best.angle) curr.best = r;
      } else {
        clusters.push(curr.best);
        curr = { startS: r.s, best: r };
      }
    }
    clusters.push(curr.best);

    // emit
    for (let n = 0; n < Math.min(capPerCurve, clusters.length); n++) {
      const c = clusters[n];
      const msg = `${label} #${wireIndex}: bend at p${c.i} is ${c.angle.toFixed(1)}° (< ${minAngleDeg}°)`;
      issues.push({ type: "tight_bend", wireIndex, atPoint: c.i, angleDeg: +c.angle.toFixed(1), threshold: minAngleDeg, message: msg });
    }
  });

  return issues;
};

/* --------------------- Component --------------------- */

const WireMeshGenerator2 = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const objRef = useRef(null);

  const horizontalGroupRef = useRef(null);
  const verticalGroupRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [wireNumber, setWireNumber] = useState(12);
  const [horizontalCurves, setHorizontalCurves] = useState([]);
  const [verticalCurves, setVerticalCurves] = useState([]);
  const [wireWarnings, setWireWarnings] = useState([]);

  const pageId = useMemo(() => `wiremesh-gen-2`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);
  const { createVisualization, removeVisualHelpers } = useVisualHelpers();

  const initScene = useCallback(() => {
    if (!containerRef.current) return;
    while (containerRef.current.firstChild) containerRef.current.removeChild(containerRef.current.firstChild);
    const cleanup = initializeThreeScene(containerRef, sceneRef, rendererRef, cameraRef, controlsRef);
    const gridHelper = new THREE.GridHelper(200, 20, 0x444444, 0x888888);
    gridHelper.position.y = 0;
    sceneRef.current.add(gridHelper);
    sceneRef.current.add(new THREE.AxesHelper(100));
    return cleanup;
  }, []);

  const computeSlicePositionsY = (bbox, spacing) => {
    const start = bbox.min.y + spacing / 2;
    const end = bbox.max.y - spacing / 2;
    const count = Math.max(1, Math.floor((end - start) / spacing));
    const adj = (end - start) / count;
    return Array.from({ length: count }, (_, i) => start + i * adj);
  };

  const computeSlicePositions = (bbox, spacing = 12) => {
    const start = bbox.min.x + spacing / 2;
    const end = bbox.max.x - spacing / 2;
    const count = Math.max(1, Math.floor((end - start) / spacing));
    const adj = (end - start) / count;
    return Array.from({ length: count }, (_, i) => start + i * adj);
  };

  const disposeGroup = useCallback((group) => {
    if (!group) return;
    group.traverse((o) => {
      if (o.isMesh) {
        o.geometry?.dispose?.();
        o.material?.dispose?.();
      }
    });
    group.clear();
    sceneRef.current?.remove(group);
  }, []);

  // reconstruct polyline in world coords from slice segments
  const buildClosedCurveWorld = (segments, uniqueVertices, matrixWorld) => {
    const worldPts = uniqueVertices.map(v => v.clone().applyMatrix4(matrixWorld));
    const used = new Set();
    const result = [];
    let curr = segments[0].i1;
    result.push(worldPts[curr]);
    let found = true;
    while (found) {
      found = false;
      for (let i = 0; i < segments.length; i++) {
        if (used.has(i)) continue;
        const seg = segments[i];
        if (seg.i1 === curr || seg.i2 === curr) {
          curr = seg.i1 === curr ? seg.i2 : seg.i1;
          result.push(worldPts[curr]);
          used.add(i);
          found = true;
          break;
        }
      }
    }
    if (!result[0].equals(result[result.length - 1])) result.push(result[0]);
    return result;
  };

  const rebuildWireMesh = useCallback(() => {
    if (!sceneRef.current || !objRef.current) return;

    disposeGroup(horizontalGroupRef.current);
    disposeGroup(verticalGroupRef.current);
    setWireWarnings([]);

    const mesh = objRef.current;
    mesh.updateMatrixWorld(true);

    const bbox = new THREE.Box3().setFromObject(mesh);
    const size = bbox.getSize(new THREE.Vector3());

    const horizontalTubes = new THREE.Group();
    const verticalTubes = new THREE.Group();

    const tubeRadius = 0.5;
    const segments = 30;
    const radial = 8;

    const horzCurves = [];
    const vertCurves = [];

    const spacingY = size.y / Math.max(1, wireNumber);
    const ySlices = computeSlicePositionsY(bbox, spacingY);
    const xSlices = computeSlicePositions(bbox, size.x / Math.max(1, wireNumber));

    // Horizontal (XZ)
    ySlices.forEach((y) => {
      const { uniqueVertices, componentSegments } = sliceModelXZ(mesh, sceneRef.current, y);
      if (!uniqueVertices || !componentSegments) return;
      componentSegments.forEach((seg) => {
        // raw world-space loop
        let pts = buildClosedCurveWorld(seg, uniqueVertices, mesh.matrixWorld);

        // CLEAN the polyline to remove micro segments / tiny bends
        pts = preprocessPolyline3(pts, {
          mergeDistMM: 0.5,
          minSegGenMM: 1.0,
          collinearDeg: 3.0,
          rdpEpsMM: 1.0,
          resampleMM: 3.0,
        });

        if (pts.length < 2) return;

        const curve = buildSafeCurvePath(pts, 1e-3);
        if (!curve) {
          // optional: console.warn("Skipped degenerate loop");
          return; // skip this loop safely
        }
        const tube = new THREE.TubeGeometry(curve, segments, tubeRadius, radial, true);
        const mat = new THREE.MeshStandardMaterial({ color: 0xff6600 });
        const tubeMesh = new THREE.Mesh(tube, mat);
        horizontalTubes.add(tubeMesh);
        horzCurves.push(curve);
      });
    });

    // Vertical (YZ)
    xSlices.forEach((x) => {
      const { uniqueVertices, componentSegments } = sliceModelYZ(mesh, sceneRef.current, x);
      if (!uniqueVertices || !componentSegments) return;
      componentSegments.forEach((seg) => {
        let pts = buildClosedCurveWorld(seg, uniqueVertices, mesh.matrixWorld);

        pts = preprocessPolyline3(pts, {
          mergeDistMM: 0.5,
          minSegGenMM: 1.0,
          collinearDeg: 3.0,
          rdpEpsMM: 1.0,
          resampleMM: 3.0,
        });
        if (pts.length < 2) return;

        const curve = buildSafeCurvePath(pts, 1e-3);
        if (!curve) {
          // optional: console.warn("Skipped degenerate loop");
          return; // skip this loop safely
        }
        const tube = new THREE.TubeGeometry(curve, segments, tubeRadius, radial, true);
        const mat = new THREE.MeshStandardMaterial({ color: 0x0066ff });
        const tubeMesh = new THREE.Mesh(tube, mat);
        verticalTubes.add(tubeMesh);
        vertCurves.push(curve);
      });
    });

    sceneRef.current.add(horizontalTubes);
    sceneRef.current.add(verticalTubes);

    horizontalGroupRef.current = horizontalTubes;
    verticalGroupRef.current = verticalTubes;

    setHorizontalCurves(horzCurves);
    setVerticalCurves(vertCurves);

    // ---- analyze after building curves ----
    const issuesH = analyzeWires(horzCurves, {
      minCurveLenMM: 20,
      minAngleDeg: 10,
      legMinMM: 15,
      windowMM: 30,
      sampleMM: 6,
      clusterMM: 35,
      capPerCurve: 6,
      label: "horizontal wire",
    });
    const issuesV = analyzeWires(vertCurves, {
      minCurveLenMM: 20,
      minAngleDeg: 10,
      legMinMM: 15,
      windowMM: 30,
      sampleMM: 6,
      clusterMM: 35,
      capPerCurve: 6,
      label: "vertical wire",
    });

    setWireWarnings([...issuesH, ...issuesV]);
  }, [disposeGroup, wireNumber]);

  const loadModel = useCallback(() => {
    setIsLoading(true);
    setError(null);

    if (objRef.current) {
      sceneRef.current.remove(objRef.current);
      objRef.current.geometry?.dispose?.();
      objRef.current.material?.dispose?.();
      objRef.current = null;
    }
    disposeGroup(horizontalGroupRef.current);
    disposeGroup(verticalGroupRef.current);

    removeVisualHelpers(sceneRef.current);

    new STLLoader().load(
      stlFile || "/models/default.stl",
      (geometry) => {
        // initial bbox
        geometry.computeBoundingBox();
        const bbox0 = geometry.boundingBox.clone();

        // center & orient
        geometry.translate(...bbox0.getCenter(new THREE.Vector3()).multiplyScalar(-1).toArray());
        geometry.rotateX(-Math.PI / 2);
        geometry.rotateY(-Math.PI / 2);

        // recompute bbox AFTER transforms
        geometry.computeBoundingBox();

        const mesh = new THREE.Mesh(
          geometry,
          new THREE.MeshStandardMaterial({
            color: 0xcccccc,
            metalness: 0.2,
            roughness: 0.6,
            transparent: true,
            opacity: 0.9,
          })
        );

        // drop to ground
        const bb = geometry.boundingBox;
        mesh.position.y = -(bb?.min.y ?? 0);
        mesh.castShadow = true;

        objRef.current = mesh;

        // show helpers & compute dims
        const bboxScene = new THREE.Box3().setFromObject(mesh);
        const size = bboxScene.getSize(new THREE.Vector3());
        setModelDimensions({ width: size.x, height: size.y, depth: size.z });
        createVisualization(mesh, sceneRef.current, bboxScene);

        // build wires after load
        rebuildWireMesh();
        setIsLoading(false);
      },
      undefined,
      (e) => {
        setError("Failed to load STL");
        setIsLoading(false);
      }
    );
  }, [stlFile, createVisualization, disposeGroup, rebuildWireMesh, removeVisualHelpers]);

  useEffect(() => {
    if (!isLoading && objRef.current) rebuildWireMesh();
  }, [wireNumber, isLoading, rebuildWireMesh]);

  useEffect(() => {
    const cleanup = initScene();
    loadModel();
    return () => {
      disposeGroup(horizontalGroupRef.current);
      disposeGroup(verticalGroupRef.current);
      if (objRef.current) {
        sceneRef.current?.remove(objRef.current);
        objRef.current.geometry?.dispose?.();
        objRef.current.material?.dispose?.();
        objRef.current = null;
      }
      cleanup && cleanup();
    };
  }, [initScene, loadModel, disposeGroup]);

  /* --------------------- CSV export --------------------- */
  const exportCSV = useCallback(() => {
    const downloadCSV = (content, filename) => {
      const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const exportLineGroup = (curves, prefix) => {
      const lines = [];
      curves.forEach((curvePath, index) => {
        const pts = curvePath.getPoints(100);
        pts.forEach((pt, i) => {
          lines.push(`${prefix}_${index + 1},${i},${pt.x.toFixed(3)},${pt.y.toFixed(3)},${pt.z.toFixed(3)}`);
        });
      });
      const csvContent = "WireID,PointIndex,X,Y,Z\n" + lines.join("\n");
      downloadCSV(csvContent, `${prefix}_wire.csv`);
    };

    exportLineGroup(horizontalCurves, "horizontal");
    setTimeout(() => {
      exportLineGroup(verticalCurves, "vertical");
      setTimeout(() => setTutorialVisible(true), 500);
    }, 500);
  }, [horizontalCurves, verticalCurves]);

  /* --------------------- UI --------------------- */
  return (
    <div className="container">
      <button className="panel-toggle-button" onClick={() => setExpanded(!expanded)}>
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

{/* warning banner */}
{wireWarnings.length > 0 && (
  <div
    style={{
      position: "fixed",
      top: 16,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 10000,
      background: "#ff9800",
      color: "#fff",
      padding: "12px 16px",
      borderRadius: 6,
      boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
      maxWidth: "92vw",
    }}
  >
    <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ Wire Checks</div>
    <div style={{ fontSize: 13, lineHeight: 1.4 }}>
      {wireWarnings.slice(0, 5).map((w, i) => (
        <div key={i}>{w.message}</div>
      ))}
      {wireWarnings.length > 5 && <div>…and {wireWarnings.length - 5} more.</div>}
      <div style={{ marginTop: 6, opacity: 0.85 }}>
        (Rules: curve ≥ 20 mm; bend ≥ 10° — tune in code if needed)
      </div>
    </div>
    
    {/* 只添加关闭按钮 */}
    <button
      onClick={() => setWireWarnings([])}
      style={{
        position: "absolute",
        top: "8px",
        right: "8px",
        background: "none",
        border: "none",
        color: "white",
        fontSize: "16px",
        cursor: "pointer",
      }}
    >
      ×
    </button>
  </div>
)}

      <div ref={containerRef} className="canvas-container"></div>

      {!isLoading && expanded && (
        <div className="controls-panel">
          <div className="panel-title-row">
            <div className="panel-title">Wire Mesh Generator 2</div>
            <span
              className="iteration-badge badge--ok"
              title="Automated wire bending is fast for quick design iteration"
            >
              Great for quick iteration
            </span>
          </div>

          <div className="panel-section">
            <div className="section-header">
              <h4>Workflow Description</h4>
            </div>
            <div className="section-content">
              <p>
                This tool generates a wireframe structure using interleaved horizontal and vertical wires based on a 3D
                model. The result is a mesh-like sculpture suitable for wire-bending fabrication.
              </p>
              <p style={{ fontStyle: "italic" }}>
                Useful for coarse representations or aesthetic wire sculptures where full solid fabrication is
                unnecessary.
              </p>
            </div>
          </div>

          <div className="section-toggle" onClick={() => setExpanded(!expanded)} style={{ cursor: "pointer" }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: "5px", transform: expanded ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.3s ease" }}
            >
              <path d="M8 12L2 6H14L8 12Z" fill="#333" />
            </svg>
            wire_structure
          </div>

          <div className="panel-content">
            <div className="input-group">
              <label>Wire_number</label>
              <div className="value-display">
                <input
                  type="range"
                  className="slider"
                  min="1"
                  max="20"
                  value={wireNumber}
                  onChange={(e) => setWireNumber(parseInt(e.target.value) || 1)}
                  disabled={isLoading}
                />
                <div className="value">{wireNumber}</div>
              </div>
            </div>
          </div>

          <div className="buttons-container">
            <button
              onClick={() => window.open("https://www.pensalabs.com/", "_blank", "noopener,noreferrer")}
              className="action-button"
            >
              WATCH BENDING DEMOS
            </button>
            <button onClick={exportCSV} className="action-button" disabled={isLoading}>
              EXPORT CSV FILES
            </button>
            <button onClick={() => setTutorialVisible(!tutorialVisible)} className="action-button">
              {tutorialVisible ? "HIDE INTERACTIVE GUIDANCE" : "SHOW INTERACTIVE GUIDANCE"}
            </button>
          </div>
        </div>
      )}

      {isLoading && <div className="loading-box">Loading model...</div>}
      {error && <div className="error-box">Error: {error}</div>}

      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={() => setTutorialVisible(!tutorialVisible)}
        tutorialContent={wireMeshTutorial}
      />
    </div>
  );
};

export default WireMeshGenerator2;
